/* eslint-disable @typescript-eslint/no-explicit-any */
import { ColumnDef } from '@tanstack/react-table';
import { useState, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useQuery } from 'react-query';
import { Offcanvas } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';

import usePageSearchParams from 'utils/usePageSearchParams';
import useAdvanceTable from 'hooks/useAdvanceTable';
import AdvanceTableProvider from 'providers/AdvanceTableProvider';
import AdvanceTable from 'components/base/AdvanceTable';
import AdvanceTableFooter from 'components/base/AdvanceTableFooter';
import customerEntityService from 'service/charterer';
import Button from 'components/base/Button';
import PageBreadcrumb from 'components/common/PageBreadcrumb';
import { PageBreadcrumbItem } from 'components/common/PageBreadcrumb';
import AddPurchaseToChild from './AddPurchaseToChild';

export type SettelementData = {
  cashier: string;
  currency: string;
  input_balance: string;
  amount: string;
  output_balance: string;
  status: string;
  bg_color: string;
  text_color: string;
  transaction_type: string;
  currency_id: string;
  original_currency_id: string;
  original_currency: string;
  original_amount: string;
  guid: string;
};

const PurchaserWithCustomerResult = () => {
  const { t, i18n } = useTranslation();

  const [openChild, setOpenChild] = useState(false);

  const { pathname, state } = useLocation();
  const [searchParams] = usePageSearchParams();

  const { name, remainder, currency_pid } = state;
  const debitor_id = pathname.split('/').pop();
  const creditor_id = searchParams.get('creditor_id');

  const [totalRemainder, setTotalRemainder] = useState<string>(
    String(remainder)
  );

  const { data, refetch } = useQuery(
    ['GET_PROVIDER_RESULT'],
    () => {
      if (debitor_id && currency_pid && creditor_id) {
        const res = customerEntityService
          .getListHistorySettlement({
            offset: 0,
            limit: 100,
            creditor_id: creditor_id,
            currency_pid: currency_pid,
            debitor_id: debitor_id
          })
          .then((res: any) => res?.debit_credit_settlements);
        return res;
      }
    },
    { enabled: !!debitor_id && !!currency_pid && !!creditor_id }
  );

  const resultData: SettelementData[] = useMemo(() => {
    return (
      data?.map((items: any) => {
        return {
          cashier:
            items?.employee?.first_name + ' ' + items?.employee?.last_name,
          input_balance: items?.balance_in
            ? new Intl.NumberFormat('en-US', {
                minimumFractionDigits: 0,
                maximumFractionDigits: 4,
                useGrouping: true
              })
                .format(items?.balance_in)
                .replace(/,/g, ' ')
            : 0,
          output_balance: items?.balance_out
            ? new Intl.NumberFormat('en-US', {
                minimumFractionDigits: 0,
                maximumFractionDigits: 4,
                useGrouping: true
              })
                .format(items?.balance_out)
                .replace(/,/g, ' ')
            : 0,
          amount: items?.amount
            ? new Intl.NumberFormat('en-US', {
                minimumFractionDigits: 0,
                maximumFractionDigits: 4,
                useGrouping: true
              })
                .format(items?.amount)
                .replace(/,/g, ' ')
            : 0,
          status: items?.status?.name?.[i18n?.language],
          currency: items?.currency?.name?.[i18n?.language],
          currency_id: items?.currency_id,
          currency_pid: items?.currency_pid,
          original_currency_id: items?.original_currency_id,
          original_currency: items?.original_currency?.name?.[i18n?.language],
          original_amount: items?.original_amount,
          transaction_type: items?.transaction_type,
          bg_color: items?.status?.background_color,
          text_color: items?.status?.text_color,
          creditor_id: items?.creditor_id,
          debitor_id: items?.debitor_id,
          guid: items?.id
        };
      }) ?? []
    );
  }, [data, t, i18n?.language]);

  const buyyersBreadcrumbItems: PageBreadcrumbItem[] = [
    {
      label: 'dt_kt',
      url: '/dtkt/provider'
    },
    {
      label: 'offsetting_buyyer',
      url: '/dtkt/purchaser-with-customer'
    },
    {
      label: name ?? '',
      active: true
    }
  ];

  const settelementDataTableColumns: ColumnDef<SettelementData>[] = [
    {
      accessorKey: 'cashier',
      header: t('cashier'),
      meta: {
        cellProps: { className: 'text-900' }
      }
    },
    {
      accessorKey: 'currency',
      header: t('currency'),
      meta: {
        cellProps: { className: 'text-900' }
      }
    },
    {
      accessorKey: 'input_balance',
      header: t('input_balance'),
      meta: {
        cellProps: { className: 'text-900' }
      }
    },
    {
      accessorKey: 'amount',
      header: t('amounts'),
      cell: rowData => {
        const {
          amount,
          currency_id,
          original_currency_id,
          original_currency,
          original_amount
        } = rowData.row.original;

        return (
          <div className="d-flex gap-2">
            <p className="mb-0">{amount}</p>
            {currency_id !== original_currency_id && (
              <p className="mb-0">
                ( {original_amount + ' ' + original_currency} )
              </p>
            )}
          </div>
        );
      },
      meta: {
        cellProps: { className: 'text-900 white-space-nowrap' }
      }
    },
    {
      accessorKey: 'output_balance',
      header: t('output_balance'),
      meta: {
        cellProps: { className: 'text-900' }
      }
    },
    {
      accessorKey: 'transaction_type',
      header: t('transaction_type'),
      cell: rowData => {
        /* prettier-ignore */
        return (
          <div>
            {rowData?.row?.original?.transaction_type === 'DEBIT'
              ? t('DEBIT')
              : rowData?.row?.original?.transaction_type === 'CREDIT'
                ? t('CREDIT')
                : rowData?.row?.original?.transaction_type === 'RETURN'
                  ? t('RETURN')
                  : t('RECALCULATION')}
          </div>
        );
      },
      meta: {
        cellProps: { className: 'text-900' }
      }
    },

    {
      accessorKey: 'status',
      header: t('status'),
      cell: rowData => {
        const { status, bg_color, text_color } = rowData?.row?.original || {};
        /* prettier-ignore */
        const defaultColors: any = {
          новый: { bg: '#1d4289', text: '#ffffff' },
          'Завершен с расхождением': { bg: '#fef3c7', text: '#92400e' },
          Yangi: { bg: '#fef3c7', text: '#92400e' },
          'Qisman tugallangan': { bg: '#fef3c7', text: '#92400e' }
        };
        /* prettier-ignore */
        const backgroundColor =
          bg_color || defaultColors[status]?.bg || '#ffffff'; // Default to white if not specified
        const textColor =
          text_color || defaultColors[status]?.text || '#000000';

        return (
          <div className="d-flex justify-content-center">
            <p
              style={{
                background: backgroundColor,
                padding: '8px 10px',
                textAlign: 'center',
                borderRadius: '8px',
                color: textColor,
                fontWeight: '600',
                margin: '0'
              }}
            >
              {status}
            </p>
          </div>
        );
      },
      meta: {
        cellProps: { className: 'text-900' }
      }
    }
  ];

  const table = useAdvanceTable({
    data: resultData,
    columns: settelementDataTableColumns,
    pageSize: 10,
    pagination: true,
    sortable: true,
    selection: false
  });

  return (
    <div>
      <PageBreadcrumb items={buyyersBreadcrumbItems} />
      <div>
        <div className="mb-5 d-flex justify-content-between">
          <h2>{name ?? ''}</h2>

          {Number(String(totalRemainder).replace(/\s/g, '')) > 0 && (
            <Button variant="primary" onClick={() => setOpenChild(true)}>
              {t('add')}
            </Button>
          )}
        </div>
        <AdvanceTableProvider {...table}>
          <div className="mt-4 mx-n4 px-4 mx-lg-n6 px-lg-6 bg-white border-top border-bottom border-200 position-relative top-1">
            <AdvanceTable tableProps={{ className: 'phoenix-table fs-9' }} />
            <AdvanceTableFooter pagination />
          </div>
        </AdvanceTableProvider>

        <Offcanvas
          show={openChild}
          onHide={() => {
            setOpenChild(false);
          }}
          placement="end"
          backdrop="static"
          keyboard={false}
        >
          <Offcanvas.Header closeButton>
            <Offcanvas.Title style={{ fontSize: '24px', fontWeight: '600' }}>
              {t('mutual_settlement')}
            </Offcanvas.Title>
          </Offcanvas.Header>
          <Offcanvas.Body>
            <AddPurchaseToChild
              setOpenChild={setOpenChild}
              refetch={refetch}
              totalRemainder={totalRemainder}
              setTotalRemainder={setTotalRemainder}
            />
          </Offcanvas.Body>
        </Offcanvas>
      </div>
    </div>
  );
};
export default PurchaserWithCustomerResult;
